<template>
  <!-- 言語選択と翻訳入力エリア -->
  <div>
    <div class="row gx-4">
      <div class="col-lg-6">
        <div class="card animate-fade-in">
          <div class="card-header p-4 pb-2">
            <label class="mb-2 fw-bold">言語を自動検出</label>
          </div>
          <hr class="my-0" />
          <div class="card-body pb-6">
            <div
              class="position-relative upload-area"
              @dragover.prevent
              @drop.prevent="handleDrop"
              @paste="handlePaste"
            >
              <!-- 装飾用ガイドテキスト -->
              <div v-if="!localInput" class="upload-placeholder text-center">
                <p class="text-muted mb-0">翻訳したい文章を張り付ける</p>
                <p class="text-muted">または</p>

                <!-- ファイル選択ボタン -->
                <label
                  class="btn bg-gradient-secondary file-button-overlay fw-bold px-4 py-2"
                >
                  <span class="fas fa-cloud-upload-alt fa-lg me-1"></span>
                  ファイルを選択ください
                  <input
                    type="file"
                    hidden
                    @change="handleFileUpload"
                    accept=".pdf,.csv,.txt,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  />
                </label>
              </div>

              <!-- 実際のテキストエリア -->
              <textarea
                class="form-control invisible-area"
                rows="11"
                v-model="localInput"
                maxlength="7000"
                @keydown.enter.exact.prevent="submitMessage"
                @keydown.enter.ctrl="insertNewLine"
                ref="localInputBody"
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6 mt-4 mt-lg-0">
        <div class="card animate-fade-in">
          <div class="card-header p-4 pt-3 pb-2">
            <div class="d-flex justify-content-between align-items-center">
              <label class="fw-bold mb-0">翻訳先言語</label>
              <select
                v-model="selectedLanguage"
                class="form-select w-auto ms-2"
                @change="submitMessage"
              >
                <option
                  v-for="language in languages"
                  :key="language"
                  :value="language"
                >
                  {{ language }}
                </option>
              </select>
            </div>
          </div>
          <hr class="my-0" />
          <div class="card-body">
            <div
              class="form-control flex-grow-1 mb-3 bg-light"
              style="min-height: 264px; overflow-y: auto"
              ref="resultOutputBody"
              v-html="parseMarkdown(resultOutput)"
            ></div>

            <div class="d-flex justify-content-end gap-2">
              <div class="input-group input-group-sm" style="width: auto">
                <!-- 左：ファイルタイプ選択 -->
                <select
                  v-model="selectedTypes"
                  class="form-select text-secondary"
                  style="max-width: 100px"
                >
                  <option value="">形式</option>
                  <option value="pptx">pptx</option>
                  <option value="xlsx">Excel</option>
                  <option value="docx">Word</option>
                </select>

                <!-- 右：ダウンロードボタン -->
                <button
                  class="btn btn-outline-secondary gap-1 mb-0"
                  type="button"
                  @click="handleOfficeFileDownload(resultOutput)"
                >
                  <i class="fas fa-download"></i>
                  ダウンロード
                </button>
              </div>
              <button
                class="btn btn-sm btn-outline-secondary px-2 mb-0"
                type="button"
                @click="copyToClipboard(resultOutput)"
              >
                <i class="fas fa-copy" style="font-size: 0.75rem"></i>
                コピーする
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg-12 mt-4">
      <div class="card">
        <!-- 右カラム：翻訳結果とコントロール -->
        <div class="card-body">
          <div :id="[`accordionRental-1`]" class="accordion">
            <div class="accordion-item">
              <div :id="[`heading-1`]" class="accordion-header">
                <button
                  class="accordion-button border-bottom font-weight-bold collapsed text-xs px-0"
                  type="button"
                  @click="toggleAccordion()"
                  data-bs-toggle="collapse"
                  :data-bs-target="[`#collapse-1`]"
                  :aria-expanded="isOpen ? 'true' : 'false'"
                  :aria-controls="[`collapse-1`]"
                >
                  <label class="fw-bold">翻訳への追加要望</label>
                  <i
                    class="collapse-close fa fa-plus text-xs pt-1 position-absolute end-0 me-3"
                    aria-hidden="true"
                    ><span class="ms-1">開く</span></i
                  >
                  <i
                    class="collapse-open fa fa-minus text-xs pt-1 position-absolute end-0 me-3"
                    aria-hidden="true"
                    ><span class="ms-1">閉じる</span></i
                  >
                </button>
              </div>
              <div
                :id="[`collapse-1`]"
                class="accordion-collapse collapse"
                :class="{ show: isOpen }"
                :aria-labelledby="[`heading-1`]"
                :data-bs-parent="[`#accordionRental-1`]"
                style=""
              >
                <div class="accordion-body text-sm px-0">
                  <div class="w-100 mt-2 d-flex justify-content-center">
                    <div class="input-group w-100 input-wrapper">
                      <textarea
                        :class="['form-control', { expanded: focused }]"
                        placeholder="例）丁寧な口調で訳してください。ビジネスメール風に訳してください。友人への返信向けに訳してください。"
                        @focus="focused = true"
                        @blur="focused = false"
                        v-model="promptValue"
                        maxlength="7000"
                      ></textarea>
                    </div>
                  </div>

                  <div class="mt-3 d-flex justify-content-end gap-3">
                    <button
                      class="btn btn-outline-secondary mb-0"
                      @click="clearAll"
                    >
                      <i class="fas fa-undo-alt me-1"></i> クリア
                    </button>
                    <button
                      class="btn bg-gradient-warning mb-0 px-4"
                      @click="submitMessage"
                    >
                      <i class="fas fa-magic me-1"></i> 実行
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TranslationPage',
  props: {
    languages: {
      type: Array,
      required: true,
    },
    userInput: {
      type: String,
      required: true,
    },
    resultOutput: {
      type: String,
      required: true,
    },
    parseMarkdown: {
      type: Function,
      required: true,
    },
  },
  emits: ['file-uploaded', 'input-message', 'handle-office-file-download'],
  data() {
    return {
      localInput: this.userInput,
      promptValue: '',
      selectedLanguage: '英語',
      focused: false,
      isOpen: false,
      selectedTypes: '',
    }
  },
  methods: {
    handleOfficeFileDownload() {
      const type = this.selectedTypes

      if (!type) {
        alert('形式を選択してください')
        return
      }

      this.$emit('handle-office-file-download', type)
    },
    toggleAccordion() {
      this.isOpen = !this.isOpen
    },
    clearAll() {
      this.promptValue = ''
    },
    copyToClipboard(text) {
      const processedText = text.replace(/\\n/g, '\n')
      navigator.clipboard.writeText(processedText)
    },
    handleFileUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.$emit('file-uploaded', file)
      }
    },
    handleDrop(event) {
      const file = event.dataTransfer.files[0]
      if (file) {
        this.$emit('file-uploaded', file)
      }
    },
    //handlePaste() {
    //  this.$nextTick(() => {
    //    this.submitMessage()
    //  })
    //},
    submitMessage() {
      const trimmed = this.localInput.trim()
      //if (trimmed) {
      this.$emit('input-message', {
        userInput: trimmed,
        promptValue: this.promptValue.trim(),
        selectedLanguage: this.selectedLanguage.trim(),
      })
      //}
    },
    insertNewLine(event) {
      const textarea = event.target
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      this.localInput =
        this.localInput.substring(0, start) +
        '\n' +
        this.localInput.substring(end)
      this.$nextTick(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 1
      })
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const result = this.$refs.resultOutputBody
        const input = this.$refs.localInputBody

        if (result) {
          result.scrollTop = result.scrollHeight
        }
        if (input) {
          input.scrollTop = input.scrollHeight
        }
      })
    },
  },
  watch: {
    userInput(newVal) {
      this.localInput = newVal.replace(/\\n/g, '\n')
    },
    localInput() {
      this.scrollToBottom()
    },
    resultOutput: {
      handler() {
        this.scrollToBottom()
      },
      deep: true,
      immediate: true,
    },
  },
}
</script>

<style scoped>
textarea.form-control {
  resize: none;
}
</style>
